import React, {
    ClipboardEvent,
    KeyboardEvent, ReactNode,
    SyntheticEvent,
    useCallback,
    useLayoutEffect,
    useRef,
    useState
} from 'react';
import intl from "react-intl-universal";
import {observer} from "mobx-react-lite";
import classNames from "classnames";

import type{AppState} from "@stores/AppState";
import {ItemModelType} from "@stores/WorkspaceStore";
import {useCrudServices} from "../../../../../platform/hooks/useServiceCombinations";
import {useAppState, useRootStore, useUserState} from "@stores/AppStateContext";
import {AddIcon} from "../../../../components/icons/AddIcon";

import {hotkeys, HOTKEYS_SCOPE, setScope} from "../../../../../utils/hotkeys";
import {undoableAddToCluster} from "../../../../../actions/item/addToCluster";
import {undoableCreateNewClusterAndAddItemToIt} from "../../../../../actions/item/createNewClusterAndAddItemToIt";
import {selectItem} from "../../../../../actions/ui/selectItem";
import {CancelIcon} from "../../../../components/icons/CancelIcon";
import {removeFromCluster} from "../../../../../actions/item/removeFromCluster";


import './Breadcrumbs.css';


export interface BreadcrumbsProps {
    item: ItemModelType;
    addition?: ReactNode;
}

/*
   bw                              <-------   --editor-width 700px  ------->
┌─────────────────────────────────────────────────────────────────────────────────────────────────────────────┐
│                        bc                        7px                                                        │
│  <-----bw_p----->    ┌──────────────────────────────────────────────────────────────────────────┐           │
│                      │         bc__pw            0              bc__pw                          │           │
│                      │        ┌───────────────────────┐       ┌───────────────────────┐         │           │
│                      │        │  bc_path              │       │  bc_path              │         │           │
│                      │        │                    ^  │       │                       │         │           │
│                      │        │                    |  │<-p_p->│                       │         │           │
│     wrapper          │        │    one_path       bch │       │      another_path     │         │           │
│                      │        │                    |  │       │                       │         │           │
│                      │        │                    v  │       │                       │         │           │
│                      │        │                       │       │                       │         │           │
│                      │        └───────────────────────┘       └───────────────────────┘         │           │
│                      │                            0                                             │           │
│                      └──────────────────────────────────────────────────────────────────────────┘           │
│                                                   7px                                                       │
└─────────────────────────────────────────────────────────────────────────────────────────────────────────────┘

 one_path paddings 5px, wrapper paddings 8px 7px;
 the goal of the complex css is to prevent vertical or horizontal shifts on mode change between editing and displaying
 we accomplish this with negative margins and margin/padding related changes.

 bw - .breadcrumbs__wrap                                       height:
 bc - .breadcrumbs                                                bch -  --breadcrumbs-base 18px
 bc__pw - breadcrumbs__path-wrap

 Css variables:
 bw_p  - --bc-outer-padding  8px
 p_p   - --bc-path-gap       6px

 bc_path plain have additional 2 * var(--bc-text-padding) gap (and negative margin) to let the nodes remain on the same place
 in editing mode
* */

export const Breadcrumbs = observer(
    ({item, addition}: BreadcrumbsProps) => {
        const appState = useAppState();
        const rootStore = useRootStore();
        const userState = useUserState();
        const crudServices = useCrudServices();

        const activeWorkspace = rootStore.view.workspace!;
        const parentPaths = item.activeParentPaths;
        const breadCrumbsRef = useRef<HTMLDivElement | null>(null);
        const breadCrumbsEditableRef = useRef<HTMLInputElement | null>(null);
        const [isEditing, setEditing] = useState<boolean>(false);
        const _isReadOnly = userState?.isCurrentUserReadOnly;

        const DEFAULT_PLACEHOLDER = intl.get('breadcrumbs.placeholder').d('Type to filter');
        const placeholderRef = useRef<string>(DEFAULT_PLACEHOLDER);

        const onSelect = useCallback((targetClusterId: string | null) => {
            if (targetClusterId !== null) {
                undoableAddToCluster(crudServices, appState, item.id, targetClusterId, activeWorkspace);
            } else {
                undoableCreateNewClusterAndAddItemToIt(crudServices, appState, activeWorkspace, item.id, rootStore.clusterFilter);
            }
            onClose();
            appState.setContextMenu(null);
        }, [activeWorkspace, item]);

        const onClose = () => {
            setEditing(false);
            rootStore.setClusterFilter('');
            setScope(HOTKEYS_SCOPE.WORKSPACE);
        }

        const handleAddToCluster = useCallback((event: React.MouseEvent<HTMLElement>) => {
            event.stopPropagation();
            if (isEditing || _isReadOnly) {
                return;
            }
            setEditing(true);
            setScope(HOTKEYS_SCOPE.BREADCRUMBS);
            appState.addAdditionalContextMenu({
                type: 'add-to-cluster',
                id: item.id,
                menuPlacement: 'block-end',
                menuAlignment: 'start',
                getTargetBoundingBox: () => breadCrumbsRef.current!.getBoundingClientRect(),
                isDark: true,
                VERTICAL_SHIFT: 0,
                HORIZONTAL_SHIFT: -2,
                minWidth: 240,
                params: {
                    itemId: item.id,
                    onSelect: onSelect
                },
                onClose: onClose,
            });
        }, [isEditing, onSelect, item, breadCrumbsRef, _isReadOnly]);

        useLayoutEffect(() => {
            if (breadCrumbsEditableRef.current) {
                breadCrumbsEditableRef.current.focus();
            }
        }, [isEditing, breadCrumbsEditableRef])

        useLayoutEffect(() => {
            hotkeys(
                'esc',
                {
                    scope: HOTKEYS_SCOPE.BREADCRUMBS,
                },
                () => {
                    onClose();
                    appState.setContextMenu(null);
                },
            )
            return () => {
                hotkeys.unbind('esc', HOTKEYS_SCOPE.BREADCRUMBS);
            }
        }, [onSelect]);

        const handleInEditClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
            event.stopPropagation();
        }, []);

        const handlePaste = useCallback((event: ClipboardEvent<HTMLDivElement>) => {
            event.preventDefault()
            const text = event.clipboardData.getData('text/plain');
            document.execCommand("insertHTML", false, text);
        }, []);

        const onInput = useCallback((event: SyntheticEvent) => {
            const node = event.target as any;
            rootStore.setClusterFilter(node.value);
        }, [])

        const handeKeyPress = useCallback((event: KeyboardEvent) => {
            if (event.key === 'Enter') {
                /* Enter is handled by AddToClusterContextMenu */
                event.preventDefault();
                event.stopPropagation();
            }
            if (event.key === 'Backspace') {
                /* on empty string backspace removes item form last cluster */
                if (rootStore.clusterFilter.length === 0) {
                    event.preventDefault();
                    event.stopPropagation();
                    const pathToDelete = parentPaths[parentPaths.length - 1];
                    if (pathToDelete) {
                        removeFromCluster(crudServices, userState!.dsManager!, item.id, pathToDelete[pathToDelete.length - 1], activeWorkspace);
                    }
                }
            }
        }, [parentPaths, item, activeWorkspace]);

        const onParentRemove = useCallback((parentId: string) => {
            removeFromCluster(crudServices, userState!.dsManager!, item.id, parentId, activeWorkspace);
        }, [item, activeWorkspace]);

        return (
            <div
                key={rootStore.currentLocale}
                className='breadcrumbs__wrap'
                onClick={handleAddToCluster}
            >
                <div
                    className={classNames({
                        'breadcrumbs': true,
                        'breadcrumbs--editing': isEditing,
                        'breadcrumbs--readOnly': _isReadOnly,
                        'breadcrumbs--single': parentPaths.length === 1,
                        'breadcrumbs--empty': parentPaths.length === 0,
                    })}
                    ref={breadCrumbsRef}
                >
                    {
                        parentPaths.length === 0
                            ? (
                                !isEditing && !_isReadOnly
                                    ? <span onClick={handleAddToCluster}
                                            className='body-medium breadcrumbs__empty'>{intl.get('breadcrumbs.add_to_cluster').d('Add to cluster')}</span>
                                    : null
                            )
                            : generateBreadcrumbPaths(appState, parentPaths, isEditing, onParentRemove)
                    }
                    {!isEditing ?
                        <span onClick={handleAddToCluster} className='breadcrumbs__plus'><AddIcon
                            size={16}/></span> : null}
                    {isEditing
                        ? (
                            <input
                                ref={breadCrumbsEditableRef}
                                className='breadcrumbs__input'
                                onKeyDown={handeKeyPress}
                                onClick={handleInEditClick}
                                onPaste={handlePaste}
                                placeholder={placeholderRef.current}
                                onChange={onInput}
                                value={rootStore.clusterFilter}
                            />
                        )
                        : null
                    }
                </div>
                {
                    addition
                        ? addition
                        : null
                }
            </div>
        )
    })

const generateBreadcrumbPaths = (appState: AppState, paths: string[][], isEditing: boolean, onParentRemove: (parentId: string) => void) => {
    const rootStore = appState.rootStore;
    const activeWorkspace = rootStore.view.workspace!;
    let i = 0;
    const isSingle = paths.length === 1;
    return paths.map((path) => (
        <div
            key={i++}
            className={classNames({
                'label-large': true,
                'breadcrumbs__path-wrap': true,
                'breadcrumbs__path-wrap--editing': isEditing,
            })}
        >
            <div
                className={classNames({
                    'breadcrumbs__path': true,
                    'breadcrumbs__path--united': !isSingle,
                })}
                onClick={isSingle ? undefined : (event) => {
                    event.stopPropagation();
                    appState.setContextMenu(null);
                    selectItem(rootStore, path[path.length - 1]);
                }}
            >
                {generatePathDetails(appState, path, isSingle)}
            </div>
            {isEditing
                ? (
                    <CancelIcon
                        className='breadcrumbs__path-remove'
                        size={14}
                        onClick={() => onParentRemove(path[path.length - 1])}
                    />
                )
                : null}
        </div>
    ))
}

const generatePathDetails = (appState: AppState, path: string[], isSingle: boolean) => {
    const rootStore = appState.rootStore;
    const activeWorkspace = rootStore.view.workspace!;
    const result = [];
    for (let i = 0; i < path.length; i++) {
        const ancestorId = path[i];
        const ancestor = activeWorkspace.items.get(ancestorId)!;
        result.push(
            <span
                key={ancestorId}
                className={
                    classNames({
                        'breadcrumbs__path-node': true,
                        'breadcrumbs__path-node--final': i === path.length - 1
                    })
                }
                onClick={isSingle ? (event) => {
                    event.stopPropagation();
                    appState.setContextMenu(null);
                    selectItem(rootStore, ancestorId);
                } : undefined}
            >{ancestor.name ? ancestor.name : ancestor.getNamePlaceholder(rootStore.currentLocale)}</span>
        );
        result.push(
            <span
                key={`${ancestorId}-d`}
                className='breadcrumbs__path-divider'
            >/</span>
        );
    }
    return result;
}
